import type {Conjunction, CriterionOperator, ICriterionFactory, OperatorableCriterion, Restriction} from '../criteria.types';
import {CRITERION_OPERATORS, isConjunction, isDisjunction, isNegation} from '../criterions/criterion-operators';
import CriterionMarshaller from './criterion-marshaller';

export default class OperatorableCriterionMarshaller {
    private singleMarshaller: CriterionMarshaller;

    constructor(private operatorableCriterionFactory: ICriterionFactory<OperatorableCriterion>) {
        this.singleMarshaller = new CriterionMarshaller(operatorableCriterionFactory);
    }

    public serialize(criterions: OperatorableCriterion[]): Conjunction {
        const conjunction = [];

        for (const criterion of criterions) {
            if (criterion.isEmpty()) {
                continue;
            }

            switch (criterion.operator) {
                case CRITERION_OPERATORS.AND_NOT:
                    conjunction.push(
                        {
                            not: this.singleMarshaller.fromCriterionToRestriction(criterion)
                        }
                    );
                    break;

                case CRITERION_OPERATORS.OR:
                    // find previous and take it with current to disjunction
                    conjunction[conjunction.length - 1] = {
                        or: [
                            conjunction[conjunction.length - 1],
                            this.singleMarshaller.fromCriterionToRestriction(criterion)
                        ]
                    };
                    break;

                case CRITERION_OPERATORS.AND:
                    conjunction.push(this.singleMarshaller.fromCriterionToRestriction(criterion));
                    break;
            }
        }

        return {
            and: conjunction
        };
    }

    public deserialize(conjunction: Conjunction): OperatorableCriterion[] {
        const arr = [];
        conjunction.and.forEach((conjunctionRestriction) => {
            this.deserializeSingle(conjunctionRestriction, arr);
        });
        return arr;
    }

    /**
     * Deserializes single restriction to possibly multiple items
     *
     * @param restriction
     * @param resultArr
     * @returns {*}
     */
    private deserializeSingle(restriction: CriterionOperator | Restriction, resultArr: OperatorableCriterion[]) {
        if (isConjunction(restriction)) {
            throw new Error('Nested conjunction is not supported');
        } else if (isDisjunction(restriction)) {
            const nestedRestrictions: OperatorableCriterion[] = [];

            restriction.or.forEach((nestedRestriction) => {
                this.deserializeSingle(nestedRestriction, nestedRestrictions);
            });

            for (let i = 1; i < nestedRestrictions.length; i++) {
                // each except first set "OR" operator
                nestedRestrictions[i].operator = CRITERION_OPERATORS.OR;
            }

            resultArr.concat(nestedRestrictions);
        } else if (isNegation(restriction)) {
            this.deserializeSingle(restriction.not, resultArr);
            // not operator to lastly added restriction
            resultArr[resultArr.length - 1].operator = CRITERION_OPERATORS.AND_NOT;
        } else {
            const crit = this.singleMarshaller.fromRestrictionToCriterion((restriction as Restriction));
            (crit as OperatorableCriterion).operator = CRITERION_OPERATORS.AND;
            resultArr.push((crit as OperatorableCriterion));
        }
    }
}
